import React, { useEffect } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import { Spinner } from 'app/src/components/Spinner';
import { IconName, Icon } from 'app/common/components/controls';
import { useFreeTrial } from './useFreeTrial';
import { Analytics } from '@trello/atlassian-analytics';
import styles from './UpsellOverlay.less';
import { FreeTrialTestIds } from '@trello/test-ids';

const formatAll = forNamespace('');
const format = forNamespace(['free trial existing', 'upsell']);

const thingsToKnow = [
  format('things-to-know-point-1'),
  format('things-to-know-point-2'),
  format('things-to-know-point-3', {
    priority: (
      <a
        href="https://help.trello.com"
        style={{ marginRight: 4 }}
        key="things-to-know-point-3-priority-support"
      >
        {format('things-to-know-point-3-priority-support')}
      </a>
    ),
  }),
  format('things-to-know-point-4'),
];

interface UpsellOverlayProps {
  onClose(): void;
  onLearnMore(): void;
  setFreeTrial?: () => void;
  orgId: string;
}

export const UpsellOverlay: React.FC<UpsellOverlayProps> = ({
  onClose,
  onLearnMore,
  orgId,
  setFreeTrial,
}) => {
  const { isLoading, startFreeTrial } = useFreeTrial({
    orgId,
    onFreeTrial: setFreeTrial,
  });

  const onLearnMoreWithTracking = () => {
    onLearnMore();
    Analytics.sendClickedButtonEvent({
      buttonName: 'learnMoreButton',
      source: 'freeTrialModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
  };

  const startFreeTrialTracking = () => {
    Analytics.sendTrackEvent({
      action: 'accepted',
      actionSubject: 'freeTrial',
      source: 'freeTrialModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
    startFreeTrial();
  };

  useEffect(() => {
    Analytics.sendScreenEvent({
      name: 'freeTrialModal',
      containers: {
        organization: {
          id: orgId,
        },
      },
    });
  }, [orgId]);

  return (
    <div className={styles.upsellOverlay}>
      <div className={styles.upsellOverlayHeader}>
        <img
          src={require('resources/images/free-trial/light.svg')}
          alt="free-trial-light"
          role="presentation"
          width="101"
          height="90"
        />
        <div className={styles.upsellOverlayTag}>
          {format(['upsell', 'tag'])}
        </div>
        <h3>{format(['upsell', 'headline'])}</h3>
        <p>{format(['upsell', 'content'])}</p>
        <Button
          className={styles.button}
          buttonType={ButtonTypes.PRIMARY}
          data-test-id={FreeTrialTestIds.StartFreeTrialButton}
          onClick={startFreeTrialTracking}
          size={ButtonSizes.FULLWIDTH}
        >
          {isLoading ? (
            <Spinner centered small wrapperClassName={styles.spinner} />
          ) : (
            format(['upsell', 'maincta'])
          )}
        </Button>
      </div>
      <div className={styles.upsellOverlayBody}>
        <div className={styles.upsellOverlayBodyHeader}>
          <h3>{format(['upsell', 'things-to-know-header'])}</h3>
          <img
            src={require('resources/images/free-trial/thumbs-up.svg')}
            alt="free-trial-thumbs-up"
            role="presentation"
            width="94"
            height="93"
          />
        </div>
        <div className={styles.upsellOverlayThings}>
          {thingsToKnow.map((thingToKnow, i) => (
            <div key={i.toString()} className={styles.upsellOverlayThing}>
              <div
                className={styles.upsellOverlayThingBullet}
                aria-hidden="true"
                role="presentation"
              />{' '}
              {thingToKnow}
            </div>
          ))}
        </div>
        <Button
          buttonType={ButtonTypes.LINKBUTTON}
          onClick={onLearnMoreWithTracking}
          size={ButtonSizes.FULLWIDTH}
        >
          {formatAll(['learn more button'])}{' '}
          <Icon name={IconName.Forward} small />
        </Button>
      </div>
    </div>
  );
};
